import React, { useEffect, useState } from 'react';
import api from '../api';
import { Plus, Trash2, Edit3, Save, X, Shield, ChevronDown, ChevronRight, Copy } from 'lucide-react';

export default function AsnResources() {
  const [asns, setAsns] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState('');

  const load = () => api.get('/asn').then(r => setAsns(r.data));
  useEffect(() => { load(); }, []);

  const del = async (a) => {
    if (!window.confirm(`Excluir ${a.asn} e todos os seus prefixos?`)) return;
    await api.delete(`/asn/${a.id}`); load();
  };

  return (
    <>
      <div className="page-title">
        <h1><Shield size={20} style={{verticalAlign:'middle'}}/> Recursos ASN (RPKI)</h1>
        <button onClick={()=>setShowAdd(true)}><Plus size={14}/> Adicionar ASN</button>
      </div>

      <p style={{color:'#a1a1aa', fontSize:'.88rem', maxWidth:'900px'}}>
        Cadastre todos os <strong>ASNs</strong> e <strong>prefixos</strong> que sua organização gerencia.
        Esses dados são usados para validar RPKI por domínio e gerar checklists prontos para registrar ROAs no NIC.br/LACNIC.
      </p>

      {asns.length === 0 ? (
        <p style={{color:'#a1a1aa', marginTop:'2rem'}}>Nenhum ASN cadastrado. Clique em "Adicionar ASN".</p>
      ) : (
        <div style={{display:'flex', flexDirection:'column', gap:'.6rem', marginTop:'1rem'}}>
          {asns.map(a => (
            <AsnCard key={a.id} asn={a} onChange={load}
              expanded={!!expanded[a.id]}
              toggle={() => setExpanded({...expanded, [a.id]: !expanded[a.id]})}
              onEdit={() => setEditing(a)}
              onDelete={() => del(a)}
            />
          ))}
        </div>
      )}

      {showAdd && <AsnModal onClose={()=>{ setShowAdd(false); load(); }} />}
      {editing && <AsnModal asn={editing} onClose={()=>{ setEditing(null); load(); }} />}

      <div style={{marginTop:'2rem', padding:'1rem', background:'#0f172a', border:'1px solid #1e3a8a', borderRadius:'8px', fontSize:'.85rem'}}>
        <strong style={{color:'#3b82f6'}}>Como usar:</strong>
        <ol style={{margin:'.5rem 0 0 1.2rem', padding:0, color:'#a1a1aa'}}>
          <li>Cadastre cada ASN da sua organização aqui</li>
          <li>Para cada ASN, adicione os prefixos (IPv4/IPv6) que ele anuncia + max-length</li>
          <li>Em cada domínio, atribua um ou mais ASNs (aba RPKI do domínio)</li>
          <li>O painel valida automaticamente todas as combinações no Routinator</li>
          <li>Use a checklist gerada para criar ROAs no Registro.br/LACNIC</li>
        </ol>
      </div>
    </>
  );
}

function AsnCard({ asn, expanded, toggle, onEdit, onDelete, onChange }) {
  const [showPx, setShowPx] = useState(false);
  const ipv4 = (asn.prefixes || []).filter(p => p.family === 'ipv4');
  const ipv6 = (asn.prefixes || []).filter(p => p.family === 'ipv6');

  const delPrefix = async (p) => {
    if (!window.confirm(`Remover ${p.prefix}?`)) return;
    await api.delete(`/asn/prefix/${p.id}`); onChange();
  };

  return (
    <div className="card" style={{padding:'1rem'}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer'}} onClick={toggle}>
        <div>
          <strong style={{fontFamily:'JetBrains Mono', fontSize:'1.1rem', color:'#3b82f6'}}>{asn.asn}</strong>
          {asn.org_name && <span style={{marginLeft:'.6rem', color:'#a1a1aa'}}>{asn.org_name}</span>}
          {asn.description && <span style={{marginLeft:'.6rem', color:'#a1a1aa', fontSize:'.78rem'}}>· {asn.description}</span>}
        </div>
        <div style={{display:'flex', gap:'.4rem', alignItems:'center'}}>
          <span style={{color:'#a1a1aa', fontSize:'.82rem'}}>
            {ipv4.length} IPv4 · {ipv6.length} IPv6
          </span>
          <button className="secondary" onClick={(e)=>{e.stopPropagation(); onEdit();}}><Edit3 size={12}/></button>
          <button className="danger" onClick={(e)=>{e.stopPropagation(); onDelete();}}><Trash2 size={12}/></button>
          {expanded ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
        </div>
      </div>

      {expanded && (
        <>
          <div style={{marginTop:'1rem', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
            <h4 style={{margin:0}}>Prefixos</h4>
            <button onClick={()=>setShowPx(true)}><Plus size={12}/> Adicionar Prefixo</button>
          </div>
          <table style={{marginTop:'.6rem'}}>
            <thead><tr><th>Prefixo CIDR</th><th>Max Length</th><th>Família</th><th>Descrição</th><th></th></tr></thead>
            <tbody>
              {(asn.prefixes || []).map(p => (
                <tr key={p.id}>
                  <td style={{fontFamily:'JetBrains Mono'}}>{p.prefix}</td>
                  <td>{p.max_length}</td>
                  <td><span className="badge ok">{p.family}</span></td>
                  <td style={{color:'#a1a1aa', fontSize:'.82rem'}}>{p.description || '–'}</td>
                  <td><button className="danger" onClick={()=>delPrefix(p)}><Trash2 size={12}/></button></td>
                </tr>
              ))}
              {(asn.prefixes || []).length === 0 && <tr><td colSpan={5} style={{color:'#a1a1aa'}}>Nenhum prefixo. Adicione um.</td></tr>}
            </tbody>
          </table>

          {showPx && <PrefixModal asn={asn} onClose={()=>{setShowPx(false); onChange();}} />}
        </>
      )}
    </div>
  );
}

function AsnModal({ asn, onClose }) {
  const editing = !!asn;
  const [form, setForm] = useState({
    asn: asn?.asn || '',
    org_name: asn?.org_name || '',
    description: asn?.description || '',
  });
  const [msg, setMsg] = useState('');

  const save = async () => {
    setMsg('');
    try {
      if (editing) await api.put(`/asn/${asn.id}`, form);
      else         await api.post(`/asn`, form);
      onClose();
    } catch (e) { setMsg(e.response?.data?.error || 'Erro'); }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <h2>{editing ? `Editar ${asn.asn}` : 'Novo ASN'}</h2>
        <label>ASN</label>
        <input value={form.asn} onChange={e=>setForm({...form, asn: e.target.value})}
          placeholder="AS65000" style={{fontFamily:'JetBrains Mono'}} />
        <label>Nome da organização</label>
        <input value={form.org_name} onChange={e=>setForm({...form, org_name: e.target.value})}
          placeholder="Hiperlink Telecom LTDA" />
        <label>Descrição (opcional)</label>
        <input value={form.description} onChange={e=>setForm({...form, description: e.target.value})}
          placeholder="AS de produção / backbone / etc" />
        {msg && <div className="err-msg">{msg}</div>}
        <div className="modal-actions">
          <button className="secondary" onClick={onClose}>Cancelar</button>
          <button onClick={save}><Save size={12}/> {editing ? 'Salvar' : 'Criar'}</button>
        </div>
      </div>
    </div>
  );
}

function PrefixModal({ asn, onClose }) {
  const [form, setForm] = useState({ prefix: '', max_length: '', description: '' });
  const [msg, setMsg] = useState('');

  const guessFamily = form.prefix.includes(':') ? 'ipv6' : 'ipv4';
  const defaultMax = guessFamily === 'ipv4' ? 32 : 128;

  const save = async () => {
    setMsg('');
    try {
      await api.post(`/asn/${asn.id}/prefix`, {
        ...form,
        max_length: form.max_length || defaultMax,
      });
      onClose();
    } catch (e) { setMsg(e.response?.data?.error || 'Erro'); }
  };

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={e=>e.stopPropagation()}>
        <h2>Novo Prefixo — {asn.asn}</h2>
        <label>Prefixo CIDR</label>
        <input value={form.prefix} onChange={e=>setForm({...form, prefix: e.target.value})}
          placeholder="203.0.113.0/24 ou 2001:db8::/32" style={{fontFamily:'JetBrains Mono'}} />
        <p style={{color:'#a1a1aa', fontSize:'.78rem', marginTop:'.3rem'}}>
          Família detectada: <strong>{form.prefix ? guessFamily : '–'}</strong>
        </p>
        <label>Max Length (ROA)</label>
        <input value={form.max_length} onChange={e=>setForm({...form, max_length: e.target.value})}
          placeholder={`Default: ${defaultMax} (ou ${guessFamily==='ipv4'?'24':'48'} recomendado)`} type="number" />
        <p style={{color:'#a1a1aa', fontSize:'.78rem', marginTop:'.3rem'}}>
          Permite anunciar prefixos até esse tamanho. Ex: prefixo /22 com maxLength=24 permite anunciar 4 /24 separados.
        </p>
        <label>Descrição</label>
        <input value={form.description} onChange={e=>setForm({...form, description: e.target.value})}
          placeholder="Ex: Bloco IPv4 principal" />
        {msg && <div className="err-msg">{msg}</div>}
        <div className="modal-actions">
          <button className="secondary" onClick={onClose}>Cancelar</button>
          <button onClick={save} disabled={!form.prefix}><Save size={12}/> Adicionar</button>
        </div>
      </div>
    </div>
  );
}
